/**
 * BackupCodesModal Component
 * 
 * Modal for displaying and regenerating backup codes
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Display backup codes in a secure format
 * - Regenerate backup codes option
 * - Copy to clipboard functionality
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Shield, Copy, RefreshCw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { securityService } from '../../../services/security-service';
import { getCurrentUserId } from '../../../lib/supabase';

interface BackupCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'view' | 'regenerate';
}

export function BackupCodesModal({ 
  isOpen, 
  onClose,
  mode = 'view'
}: BackupCodesModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  // Load backup codes when modal opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'view') {
        loadBackupCodes();
      } else {
        // For regenerate mode, show confirmation first
        setShowRegenerateConfirm(true);
      }
    }
  }, [isOpen, mode]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBackupCodes([]);
      setError('');
      setCopiedIndex(null);
      setShowRegenerateConfirm(false);
    }
  }, [isOpen]);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, loading, onClose]);

  const loadBackupCodes = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('You must be logged in to view backup codes');
        return;
      }

      const response = await securityService.getBackupCodes(userId);
      
      if (response.error) {
        setError(response.error.message || 'Failed to load backup codes');
        return;
      }

      if (response.data) {
        setBackupCodes(response.data.backupCodes);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load backup codes');
    } finally {
      setLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('You must be logged in to regenerate backup codes');
        return;
      }

      const response = await securityService.regenerateBackupCodes(userId);
      
      if (response.error) {
        setError(response.error.message || 'Failed to regenerate backup codes');
        return;
      }

      if (response.data) {
        setBackupCodes(response.data.backupCodes);
        setShowRegenerateConfirm(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (code: string, index: number) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="backup-codes-title"
      aria-describedby="backup-codes-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
                <Shield className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              </div>
              <div>
                <h2 id="backup-codes-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {mode === 'regenerate' ? 'Regenerate Backup Codes' : 'Backup Codes'}
                </h2>
                <p id="backup-codes-description" className="text-sm text-gray-600 dark:text-gray-400">
                  {mode === 'regenerate' 
                    ? 'Generate new backup codes (old codes will be invalidated)'
                    : 'Save these codes in a secure location'}
                </p>
              </div>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {showRegenerateConfirm && mode === 'regenerate' ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Regenerating backup codes will invalidate your current codes
                      </p>
                      <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
                        Make sure you have saved your current backup codes before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button 
                    type="button" 
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    onClick={regenerateBackupCodes}
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Regenerate Codes
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : loading && backupCodes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                  {mode === 'regenerate' ? 'Regenerating backup codes...' : 'Loading backup codes...'}
                </p>
              </div>
            ) : error ? (
              <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              </div>
            ) : backupCodes.length > 0 ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Important:</strong> Save these backup codes in a secure location. 
                    You can use them to access your account if you lose access to your authenticator app.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {backupCodes.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800"
                    >
                      <code className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">
                        {code}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(code, index)}
                        className="ml-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        aria-label={`Copy code ${index + 1}`}
                      >
                        {copiedIndex === index ? (
                          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>

                {mode === 'view' && (
                  <div className="flex justify-end space-x-3 mt-6">
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowRegenerateConfirm(true);
                      }}
                      className="btn-secondary"
                      disabled={loading}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate Codes
                    </button>
                    <button 
                      type="button" 
                      onClick={onClose}
                      className="btn-primary"
                    >
                      Done
                    </button>
                  </div>
                )}

                {mode === 'regenerate' && (
                  <div className="flex justify-end space-x-3 mt-6">
                    <button 
                      type="button" 
                      onClick={onClose}
                      className="btn-primary"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No backup codes available. Please regenerate them.
                </p>
                <button 
                  type="button" 
                  onClick={regenerateBackupCodes}
                  className="btn-primary mt-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate Codes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

